import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Loader2, AlertCircle, ShoppingCart,
  Printer, FileText,
} from 'lucide-react';
import { useMyOrdineDetail } from '../../hooks/useMyOrdini';
import { aggiornaContatori } from '../../hooks/useDashboardCounters';
import { useCart } from '../../context/CartContext';
import { productService } from '../../services/productService';
import { toast } from 'sonner';
import type { Product } from '../../types';
import { statoOrdine, statoRiga } from '../../utils/statoOrdine';
import { articoliEPezzi } from '../../utils/articoliEPezzi';
import { scontoRiga, righeConSconto } from '../../utils/scontoRiga';
import api from '../../services/api';
import { VettoreLogo } from '../../components/portal/VettoreLogo';

/* ── helpers ── */
const fmtEur = (n: number) => `€ ${n.toFixed(2).replace('.', ',')}`;
const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('it-IT') : '—';

/* ── Timeline (disabilitata — mantenuta per riferimento futuro)
const STEPS = ['Ricevuto', 'In lavoraz.', 'Confermato', 'Spedito', 'In viaggio', 'Consegnato'];

function stepsCompleted(status: string): number {
  if (status === 'consegnato') return 6;
  if (status === 'spedito')    return 4;
  if (status === 'confermato') return 3;
  return 1;
}

function OrderTimeline({ status }: { status: string }) {
  const done = stepsCompleted(status);
  const pct = ((done - 1) / (STEPS.length - 1)) * 100;
  return (
    <div className="py-2 px-2">
      <div className="relative flex items-start justify-between">
        <div className="absolute left-0 right-0 top-[10px] h-[3px] bg-muted/40">
          <div
            className="h-full bg-[hsl(210,64%,29%)] transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        {STEPS.map((step, i) => {
          const isDone = i < done;
          const isCurrent = i === done - 1;
          return (
            <div key={step} className="relative flex flex-col items-center gap-2 z-10">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors
                ${isDone
                  ? 'bg-[hsl(210,64%,29%)] border-[hsl(210,64%,29%)]'
                  : 'bg-white border-[hsl(0,0%,78%)]'
                }`}>
                {isCurrent
                  ? <CheckCircle2 className="w-3 h-3 text-white" />
                  : isDone
                    ? <div className="w-2 h-2 rounded-full bg-white" />
                    : null}
              </div>
              <span className={`text-[9px] sm:text-[10px] font-semibold uppercase tracking-wide text-center leading-none whitespace-nowrap
                ${isDone ? 'text-[hsl(210,64%,29%)]' : 'text-muted-foreground/50'}`}>
                {step}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
── */

/* ── Nuova card stato — flag NTS ── */

function OrderStatusCard({ status }: { status: string }) {
  const meta = statoOrdine(status);
  return (
    <div className="rounded-xl border bg-white overflow-hidden" data-testid="order-status-card">
      <div className="px-4 py-2.5 bg-[hsl(210,40%,97%)] border-b">
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Stato dell'ordine</h2>
      </div>
      <div className="px-4 py-3">
        <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-semibold ${meta.badge}`}>
          <span className={`w-2 h-2 rounded-full flex-none ${meta.dot}`} />
          {meta.label}
        </span>
      </div>
    </div>
  );
}

/* ── Section wrapper ── */
function Section({ title, children, noPad }: { title: string; children: React.ReactNode; noPad?: boolean }) {
  return (
    <div className="rounded-xl border bg-white overflow-hidden">
      <div className="px-4 py-2.5 bg-[hsl(210,40%,97%)] border-b">
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</h2>
      </div>
      <div className={noPad ? '' : 'p-4'}>{children}</div>
    </div>
  );
}

interface SpedizioneOrdine {
  numero: string;
  data: string | null;
  tracking: string | null;
  vettore: string | null;
  url: string | null;
  key?: { tipork: string; anno: number; serie: string; numdoc: number };
}
interface SpedizioniOrdineResp { items: SpedizioneOrdine[] }

/* ────────────────────────────────────────────── */
export default function OrderDetailPage() {
  const { tipork, anno, serie, numdoc } = useParams<{
    tipork: string; anno: string; serie: string; numdoc: string;
  }>();

  // useMemo stabilizza il riferimento all'oggetto per evitare loop infiniti nell'hook
  const key = useMemo(
    () => (tipork && anno && serie && numdoc)
      ? { tipork, anno: Number(anno), serie, numdoc: Number(numdoc) }
      : null,
    [tipork, anno, serie, numdoc]
  );

  const { data: ordine, loading, error } = useMyOrdineDetail(key);

  // Dalla conferma d'ordine, "Stampa ordine" apre qui con `?stampa=1` (#6).
  // Il parametro si toglie subito, così un ricaricamento non ristampa; la
  // richiesta resta nel ref (togliendolo l'effetto riparte, e un cleanup
  // annullerebbe la stampa).
  const [searchParams, setSearchParams] = useSearchParams();
  const stampaRichiesta = useRef(searchParams.get('stampa') === '1');
  useEffect(() => {
    if (!ordine || !stampaRichiesta.current) return;
    stampaRichiesta.current = false;
    setSearchParams({}, { replace: true });
    setTimeout(() => window.print(), 300);
  }, [ordine, setSearchParams]);

  // D.D.T. dell'ordine con vettore e tracciamento (osservazioni 30/10, #14):
  // solo quando qualcosa è partito, perché li cerca fra i documenti.
  const [spedizioni, setSpedizioni] = useState<SpedizioneOrdine[] | null>(null);
  const spedito = !!ordine?.items?.some(it => (it.quantitaEvasa || 0) > 0);
  useEffect(() => {
    if (!key || !spedito) return;
    api.get<SpedizioniOrdineResp>(
      `/api/clienti/me/ordini/${key.tipork}/${key.anno}/${encodeURIComponent(key.serie)}/${key.numdoc}/spedizioni`)
      .then(r => setSpedizioni(r.data.items))
      .catch(() => setSpedizioni([]));
  }, [key, spedito]);
  // Aprire l'ordine lo segna visto (lo fa il backend): il chip si aggiorna.
  useEffect(() => { if (ordine) aggiornaContatori(); }, [ordine]);
  const { addItem } = useCart();

  const handleReorderAll = async () => {
    const items = (ordine?.items ?? []).filter(it => it.sku);
    // La riga d'ordine non porta né giacenza né vendibilità: senza il
    // prodotto corrente ``addItem`` non potrebbe fermarsi alla disponibilità.
    let current = new Map<string, Product>();
    if (items.length === 0) { toast.success('Nessun articolo trovato.'); return; }
    try {
      const skus = Array.from(new Set(items.map(it => it.sku as string)));
      const res = await productService.list({ skus: skus.join(','), limit: Math.max(1, skus.length) });
      current = new Map(res.items.map(p => [p.sku, p]));
    } catch {
      toast.error('Impossibile verificare la disponibilità: riprova.');
      return;
    }
    let added = 0;
    for (const it of items) {
      const p = current.get(it.sku as string);
      const product = {
        sku: it.sku, title: it.title, brand: '',
        category: '', compatibility: '', type: 'Compatibile',
        yield: '', color: '',
        listino10: it.prezzoUnitario,
        stock: p?.stock, webSellable: p?.webSellable,
      } as unknown as Product;
      if (addItem(product, Math.max(1, Math.round(it.quantita))) > 0) added++;
    }
    if (added > 0) toast.success(`${added} articoli aggiunti al carrello`);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16 text-muted-foreground">
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento ordine…
    </div>
  );

  if (error || !ordine) return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-800 flex items-center gap-2">
      <AlertCircle className="w-4 h-4" />
      {error ?? 'Ordine non trovato.'}
      <Link to="/area-clienti/ordini" className="ml-auto underline text-xs">← Tutti gli ordini</Link>
    </div>
  );

  const items = ordine.items ?? [];
  const conSconto = righeConSconto(items);

  return (
    <div data-testid="order-detail-page" className="space-y-4">

      {/* ── Action bar ── */}
      <div className="no-print flex flex-wrap items-center gap-2">
        <Link to="/area-clienti/ordini"
          data-testid="order-back-btn"
          className="h-9 px-3 rounded-lg border bg-white text-sm font-medium inline-flex items-center gap-1.5 hover:bg-[hsl(210,40%,97%)] transition-colors">
          <ArrowLeft className="w-4 h-4" /> Tutti gli ordini
        </Link>
        <div className="flex-1" />
        <button onClick={() => window.print()}
          data-testid="order-print-btn"
          className="h-9 px-3 rounded-lg border bg-white text-sm font-medium inline-flex items-center gap-1.5 hover:bg-[hsl(210,40%,97%)] transition-colors">
          <Printer className="w-4 h-4" /> Stampa
        </button>
        <button onClick={handleReorderAll} disabled={items.length === 0}
          data-testid="order-reorder-btn"
          className="h-9 px-4 rounded-lg bg-[hsl(210,64%,29%)] text-white text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-[hsl(210,64%,22%)] transition-colors disabled:opacity-40">
          <ShoppingCart className="w-4 h-4" /> Riacquista tutto
        </button>
      </div>

      {/* ── Header ordine ── */}
      <div className="rounded-xl border bg-white px-4 py-3 flex flex-wrap items-center gap-3">
        <div>
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Ordine</p>
          <p className="font-mono font-bold text-lg leading-none mt-0.5">{ordine.id}</p>
        </div>
        <div className="h-8 w-px bg-border hidden md:block" />
        <div>
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Data</p>
          <p className="text-sm font-medium mt-0.5">{fmtDate(ordine.date)}</p>
        </div>
        {ordine.carrier && (
          <>
            <div className="h-8 w-px bg-border hidden md:block" />
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Vettore</p>
              <p className="text-sm mt-0.5 flex items-center gap-1">
                <VettoreLogo nome={ordine.carrier} className="h-4" />
              </p>
            </div>
          </>
        )}
        {ordine.destination && (
          <>
            <div className="h-8 w-px bg-border hidden md:block" />
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Destinazione</p>
              <p className="text-sm font-medium mt-0.5">{ordine.destination}</p>
            </div>
          </>
        )}
        <div className="ml-auto text-right">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Totale ordine</p>
          <p className="font-bold text-xl tabular-nums mt-0.5">{fmtEur(ordine.totaleIvato)}</p>
          <p className="text-[10px] text-muted-foreground">IVA inclusa</p>
          {ordine.totale != null && (
            <p className="text-[11px] text-muted-foreground tabular-nums">{fmtEur(ordine.totale)} IVA esclusa</p>
          )}
        </div>
      </div>

      {/* ── Stato ordine (vecchia Timeline — commentata, mantenuta per riferimento)
      <Section title="Stato dell'ordine">
        <OrderTimeline status={ordine.status} />
      </Section>
      ── */}

      {/* ── Nuova card stato ordine ── */}
      <OrderStatusCard status={ordine.status} />

      {/* ── Riepilogo prodotti ── */}
      {items.length > 0 && (
        <Section title="Riepilogo ordine" noPad>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-[11px] text-muted-foreground uppercase tracking-wide bg-[hsl(210,40%,98%)]">
                  <th className="text-left px-4 py-2.5 font-semibold">Prodotto</th>
                  <th className="text-left px-3 py-2.5 font-semibold hidden md:table-cell">Codice</th>
                  <th className="text-center px-3 py-2.5 font-semibold hidden md:table-cell">Stato sped.</th>
                  <th className="text-right px-3 py-2.5 font-semibold hidden md:table-cell">Prezzo/Ud.</th>
                  {conSconto && <th className="text-right px-3 py-2.5 font-semibold hidden md:table-cell">Sconto</th>}
                  <th className="text-right px-3 py-2.5 font-semibold">Qtà</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Totale</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {items.map(item => {
                  // Lo stato della riga, non quello dell'ordine (INT-131).
                  const shipStatus = statoRiga(item, ordine.status);
                  return (
                    <tr key={item.riga} className="hover:bg-[hsl(210,40%,99%)] transition-colors">
                      <td className="px-4 py-3 max-w-[240px]">
                        <Link to={`/prodotti/_/${item.sku}`}
                          className="font-medium hover:text-[hsl(210,64%,29%)] hover:underline leading-snug line-clamp-2">
                          {item.title}
                        </Link>
                        {/* Mobile: codice + stato */}
                        <div className="md:hidden mt-1 flex items-center gap-2">
                          <span className="font-mono text-[10px] text-muted-foreground">{item.sku}</span>
                          <span className={`text-[10px] font-semibold px-1.5 py-px rounded border ${shipStatus.badge}`}>
                            {shipStatus.label}
                          </span>
                          {scontoRiga(item.scont1, item.scont2) && (
                            <span className="text-[10px] text-muted-foreground">
                              {fmtEur(item.prezzoUnitario)} {scontoRiga(item.scont1, item.scont2)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 hidden md:table-cell">
                        <Link to={`/prodotti/_/${item.sku}`}
                          className="font-mono text-xs text-[hsl(210,64%,29%)] hover:underline">
                          {item.sku}
                        </Link>
                      </td>
                      <td className="px-3 py-3 text-center hidden md:table-cell">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border whitespace-nowrap ${shipStatus.badge}`}>
                          {shipStatus.label}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right hidden md:table-cell font-mono tabular-nums text-muted-foreground">
                        {fmtEur(item.prezzoUnitario)}
                      </td>
                      {conSconto && (
                        <td className="px-3 py-3 text-right hidden md:table-cell font-mono tabular-nums text-muted-foreground"
                          data-testid={`order-riga-sconto-${item.riga}`}>
                          {scontoRiga(item.scont1, item.scont2) ?? '—'}
                        </td>
                      )}
                      <td className="px-3 py-3 text-right">
                        <div className="text-xs leading-snug">
                          <div>Ord.: <span className="font-semibold">{item.quantita}</span></div>
                          <div className="text-muted-foreground">Sped.: {item.quantitaEvasa || 0}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold tabular-nums">
                        {fmtEur(item.totaleRiga)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Totali */}
          <div className="px-4 pb-4 pt-3 border-t flex justify-end">
            <div className="w-64 space-y-1.5 text-sm">
              <div className="flex justify-between" data-testid="order-articoli">
                <span className="text-muted-foreground">Articoli</span>
                <span>{articoliEPezzi(items.length, items.reduce((s, i) => s + (i.quantita || 0), 0))}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotale</span>
                <span className="font-mono tabular-nums">{fmtEur(ordine.totalNet)}</span>
              </div>
              {ordine.speseSpedizione > 0 && (
                <div className="flex justify-between">
                  {/* ``speseSpedizione`` è ``et_speinc`` di NTS: spese d'incasso (INT-63). */}
                  <span className="text-muted-foreground">Spese d&apos;incasso</span>
                  <span className="font-mono tabular-nums">{fmtEur(ordine.speseSpedizione)}</span>
                </div>
              )}
              {/* Entrambi i totali (INT-130): IVA esclusa, se il gestionale ha
                  reso il dettaglio, e IVA inclusa, come negli elenchi. */}
              <div className="flex justify-between pt-2 border-t">
                <span className="text-muted-foreground">Totale IVA esclusa</span>
                <span className="font-mono tabular-nums">{ordine.totale != null ? fmtEur(ordine.totale) : '—'}</span>
              </div>
              <div className="flex justify-between items-baseline font-bold text-base">
                <span>Totale IVA inclusa</span>
                <span className="font-mono tabular-nums text-lg">{fmtEur(ordine.totaleIvato)}</span>
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* ── Spedizione + Fatturazione ── */}
      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Spedizione">
          <div className="space-y-3 text-sm">
            {ordine.destination && (
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">Destinazione</p>
                <p className="font-medium">{ordine.destination}</p>
              </div>
            )}
            {ordine.carrier && (
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">Metodo di spedizione</p>
                <p className="flex items-center gap-1.5">
                  <VettoreLogo nome={ordine.carrier} />
                </p>
              </div>
            )}
            {spedizioni && spedizioni.length > 0 && (
              <div data-testid="order-spedizioni">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Spedizioni</p>
                <ul className="space-y-2">
                  {spedizioni.map(sp => (
                    <li key={sp.numero} className="rounded-lg border px-3 py-2 space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        {sp.key ? (
                          <Link to={`/area-clienti/documenti/${sp.key.tipork}/${sp.key.anno}/${encodeURIComponent(sp.key.serie)}/${sp.key.numdoc}`}
                            className="font-mono text-xs font-semibold text-[hsl(210,64%,29%)] hover:underline">{sp.numero}</Link>
                        ) : <span className="font-mono text-xs font-semibold">{sp.numero}</span>}
                        {sp.data && <span className="text-xs text-muted-foreground">{sp.data}</span>}
                      </div>
                      {sp.vettore && <VettoreLogo nome={sp.vettore} className="h-4" />}
                      {sp.tracking && (
                        <div className="flex items-center gap-2 flex-wrap text-xs">
                          <span className="font-mono text-muted-foreground select-all">{sp.tracking}</span>
                          {sp.url && (
                            <a href={sp.url} target="_blank" rel="noopener noreferrer" data-testid="order-tracking-link"
                              className="no-print text-[hsl(210,64%,29%)] hover:underline">Segui la spedizione</a>
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!ordine.destination && !ordine.carrier && (
              <p className="text-muted-foreground text-xs italic">Dati spedizione non disponibili.</p>
            )}
          </div>
        </Section>

        <Section title="Fatturazione">
          <div className="space-y-3 text-sm">
            {ordine.paymentMethod && (
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">Metodo di pagamento</p>
                <p className="font-medium">{ordine.paymentMethod}</p>
              </div>
            )}
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">Documenti</p>
              <Link to="/area-clienti/documenti"
                className="text-[hsl(210,64%,29%)] hover:underline text-sm inline-flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" /> Vedi fatture e DDT
              </Link>
            </div>
          </div>
        </Section>
      </div>

      {/* ── Note ── */}
      {ordine.note && (
        <Section title="Note ordine">
          <p className="text-sm text-muted-foreground italic">{ordine.note}</p>
        </Section>
      )}

    </div>
  );
}
